<template>
  <div class="page-wrap">
    <div v-if="loaded">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="page-header__left">
          <button class="btn-back" @click="$router.back()">
            <i class="fa fa-arrow-left"></i>
          </button>
          <div>
            <h1 class="page-title">Gift #{{ gift.id }}</h1>
            <div class="page-meta">
              <span class="meta-item"><i class="fa fa-box"></i> {{ gift.title }}</span>
              <span class="meta-item"><i class="fa fa-map-marker"></i> {{ gift.city }}</span>
              <span class="meta-item"><i class="fa fa-tag"></i> {{ gift.type }}</span>
              <span
                  class="meta-item meta-item--clickable"
                  v-if="gift.meta && gift.meta.sub_total"
                  @click="changePrice()"
                  title="Click to edit price"
              >
                <i class="fa fa-pencil"></i> {{ gift.meta.sub_total }} AED
              </span>
              <span class="touched-badge" v-if="gift.meta && gift.meta.touched" title="Customer has used this gift partly or completely.">
                <i class="fa fa-pen"></i> Touched
              </span>
            </div>
          </div>
        </div>
        <div class="page-header__right">
          <span v-html="statusBadge"></span>
        </div>
      </div>

      <!-- ── Info Grid ── -->
      <div class="info-grid">
        <div class="info-card">
          <div class="info-card__label">Customer Name</div>
          <div class="info-card__value">{{ gift.customer_name || '—' }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Customer Country</div>
          <div class="info-card__value">{{ gift.customer_country || '—' }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Purchase Date</div>
          <div class="info-card__value">{{ formatDate(gift.created_at) || '—' }}</div>
        </div>
        <div class="info-card" id="tooltip-target-1">
          <div class="info-card__label">Expiration Date</div>
          <div class="info-card__value">{{ gift.expires_at || '—' }}</div>
          <b-tooltip target="tooltip-target-1" triggers="hover" placement="bottom">
            <div v-html="giftExpirationHistory"></div>
          </b-tooltip>
        </div>
        <div class="info-card">
          <div class="info-card__label">Refcode / Password</div>
          <div class="info-card__value mono">{{ gift.ref_code }} / {{ gift.password }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Recipient Email</div>
          <div class="info-card__value">{{ gift.details.email || '—' }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Passengers</div>
          <div class="info-card__value">{{ passengers }}</div>
        </div>
        <div class="info-card info-card--link" @click="$router.push(`/carts/${gift.meta.cart_id}`)">
          <div class="info-card__label">Cart ID</div>
          <div class="info-card__value link-cell">#{{ gift.meta.cart_id }}</div>
        </div>
        <div class="info-card info-card--link" v-if="gift.meta.invoice_id" @click="$router.push(`/invoices/${gift.meta.invoice_id}`)">
          <div class="info-card__label">Invoice Code</div>
          <div class="info-card__value link-cell">DD00{{ gift.meta.invoice_code }}</div>
        </div>
        <div class="info-card info-card--link" v-if="gift.meta.order_id" @click="$router.push(`/orders/${gift.meta.order_id}`)">
          <div class="info-card__label">Order ID</div>
          <div class="info-card__value link-cell">#{{ gift.meta.order_id }}</div>
        </div>
      </div>

      <!-- ── Cart Items Table ── -->
      <div class="panel" v-if="cart && cart.items">
        <div class="panel-title"><i class="fa fa-shopping-cart"></i> Cart Items</div>
        <div class="table-responsive-wrap">
          <table class="dd-table">
            <thead>
            <tr>
              <th>Product</th>
              <th>Bookings / Quantity</th>
            </tr>
            </thead>
            <tbody>
            <tr
                v-for="item in cart.items"
                :key="item.id"
                v-if="item.options.product_id === gift.product_id"
            >
              <td class="item-title">{{ item.meta['title'] }}</td>
              <td>{{ item.options.details['number_of_bookings'] ?? '—' }} / {{ item.options.details['quantity'] ?? '—' }}</td>
            </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── Gift Message ── -->
      <div class="panel">
        <div class="panel-title"><i class="fa fa-envelope"></i> Gift Message</div>
        <div class="message-grid">
          <div class="message-field">
            <div class="message-field__label">From</div>
            <div class="message-field__value">{{ gift.details.from || '—' }}</div>
          </div>
          <div class="message-field">
            <div class="message-field__label">To</div>
            <div class="message-field__value">{{ gift.details.to || '—' }}</div>
          </div>
          <div class="message-field message-field--full" v-if="gift.details.message">
            <div class="message-field__label">Message</div>
            <div class="message-field__value message-field__value--msg">{{ gift.details.message }}</div>
          </div>
        </div>
        <button class="btn-print" @click="print()">
          <i class="fa fa-print"></i> Print
        </button>
      </div>

      <!-- ── Delivery Info (giftbox) ── -->
      <div class="panel" v-if="gift.type === 'giftbox'">
        <div class="panel-title"><i class="fa fa-truck"></i> Delivery Information</div>
        <div class="info-grid info-grid--sm">
          <div class="info-card">
            <div class="info-card__label">Delivery Date</div>
            <div class="info-card__value">{{ gift.details.delivery_date || '—' }}</div>
          </div>
          <div class="info-card">
            <div class="info-card__label">Phone Number</div>
            <div class="info-card__value">{{ gift.details.delivery_phone_number || '—' }}</div>
          </div>
          <div class="info-card" style="grid-column: span 2;">
            <div class="info-card__label">Delivery Address</div>
            <div class="info-card__value">
              {{ gift.details.delivery_address.city_title }}: {{ gift.details.delivery_address.address }}
              ({{ gift.details.delivery_address.post_code }})
            </div>
          </div>
        </div>
      </div>

      <!-- ── Comments ── -->
      <div class="panel" v-if="gift.comments">
        <div class="panel-title"><i class="fa fa-comment"></i> Comments</div>
        <div class="comments-text">{{ gift.comments }}</div>
      </div>

      <!-- ── Exchange Transaction ── -->
      <div class="panel" v-if="transaction">
        <div class="panel-title"><i class="fa fa-exchange"></i> Exchange Transaction Info</div>
        <div class="transaction-row">
          <router-link :to="{ name: 'user.show', params: { id: transaction.user.id } }" class="link-cell">
            <i class="fa fa-user"></i> {{ transaction.user.full_name }}
          </router-link>
          <router-link :to="{ name: 'user.wallet_history', params: { id: transaction.user.id } }" class="link-cell">
            <i class="fa fa-history"></i> Credit History
          </router-link>
          <span class="transaction-amount">
            <i class="fa fa-coins"></i> {{ transaction.amount }} AED
          </span>
          <span class="status-badge status-badge--green">{{ transaction.status }}</span>
          <span class="status-badge status-badge--amber">{{ transaction.type }}</span>
        </div>
      </div>

      <!-- ── Action Buttons ── -->
      <div class="actions-bar">
        <button
            class="action-btn action-btn--red"
            @click.prevent="showExchangeModal()"
            v-if="gift.status === 'Open'"
        >
          <i class="fa fa-exchange"></i> Exchange Gift
        </button>
        <button
            class="action-btn action-btn--green"
            @click.prevent="$modal.show('renew-modal')"
            v-if="!['Dated', 'Canceled', 'Exchanged'].includes(gift.status)"
        >
          <i class="fa fa-calendar-plus-o"></i> Extend Expiration
        </button>
        <button
            class="action-btn action-btn--amber"
            @click.prevent="cancel()"
            v-if="gift.status === 'Open'"
        >
          <i class="fa fa-ban"></i> Cancel Gift
        </button>
        <button
            class="action-btn action-btn--secondary"
            @click.prevent="$modal.show('comments-modal')"
        >
          <i class="fa fa-comment"></i> Edit Comments
        </button>
      </div>

    </div>

    <!-- Loading -->
    <div class="mt-5 pt-5">
      <loading v-if="!loaded"></loading>
    </div>

    <!-- ── Exchange Modal ── -->
    <modal name="exchange-modal" height="auto" v-if="loaded">
      <div class="dd-modal">
        <div class="dd-modal__header">
          <span class="dd-modal__title">Exchange Gift</span>
          <button class="dd-modal__close" @click="$modal.hide('exchange-modal')">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <div class="dd-modal__body">
          <p class="dd-modal__desc">Deposit <strong>{{ gift.meta.sub_total }} AED</strong> credit into wallet of...</p>

          <div class="radio-group">
            <label class="radio-option">
              <input name="email" v-model="exchangeForm.email" type="radio" :value="gift.customer_email" />
              <span>Customer: <strong>{{ gift.customer_email }}</strong></span>
            </label>
            <label class="radio-option">
              <input name="email" v-model="exchangeForm.email" type="radio" :value="gift.details.email" :disabled="!gift.details.email" />
              <span>Recipient: <strong>{{ gift.details.email || '—' }}</strong></span>
            </label>
          </div>

          <div class="divider-label">or search for another user:</div>
          <search-box
              @typing="searchUser"
              @selected="selectSearchedUser"
              :results="exchangeForm.searchedEmails"
              placeholder="Search by email, phone or name"
              class="w-100"
          ></search-box>
          <label class="radio-option mt-2" v-if="exchangeForm.searchedEmail">
            <input name="email" v-model="exchangeForm.email" type="radio" :value="exchangeForm.searchedEmail" />
            <span>{{ exchangeForm.searchedEmail }}</span>
          </label>

          <ul class="error-list" v-if="exchangeForm.errors.length">
            <li v-for="error in exchangeForm.errors" :key="error">{{ error }}</li>
          </ul>
        </div>
        <div class="dd-modal__footer">
          <button class="btn-cancel" @click="$modal.hide('exchange-modal')">Cancel</button>
          <button class="btn-submit btn-submit--red" @click.prevent="exchange()">
            <i class="fa fa-check"></i> Confirm Exchange
          </button>
        </div>
      </div>
    </modal>

    <modal name="renew-modal" height="auto" v-if="loaded">
      <renew-modal :giftId="gift.id" @done="loadData()"></renew-modal>
    </modal>

    <modal name="comments-modal" height="auto" v-if="loaded">
      <comments-modal :giftId="gift.id" :giftComments="gift.comments" @done="loadData()"></comments-modal>
    </modal>

  </div>
</template>

<script>
import _ from "lodash";
import { config } from "../helpers";
import SearchBox from "../components/SearchBox.vue";
import RenewModalVue from "../components/ViewComponents/Gifts/RenewModal.vue";
import GiftCommentsModalVue from "../components/ViewComponents/Gifts/GiftCommentsModal.vue";

export default {
  name: "Gift",
  components: {
    "search-box": SearchBox,
    "renew-modal": RenewModalVue,
    "comments-modal": GiftCommentsModalVue,
  },
  data() {
    return {
      id: this.$route.params.id,
      loaded: false,
      gift: null,
      cart: null,
      transaction: null,
      exchangeForm: {
        email: "",
        searchedUsers: [],
        searchedEmail: "",
        errors: [],
      },
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin";
    this.loadData();
  },

  methods: {
    loadData() {
      axios.get(`${this.url}/gifts/${this.id}`).then(async (response) => {
        this.gift = response.data.data;
        await this.getCart();
        this.loaded = true;
        if (this.gift.status === "Exchanged") this.getTransaction();
      });
    },

    getTransaction() {
      axios.get(`${this.url}/transactions?gift_id=${this.gift.id}`).then((r) => {
        const res = r.data.data;
        if (res) this.transaction = res[0];
      });
    },

    async getCart() {
      axios.get(`${this.url}/carts/${this.gift.meta["cart_id"]}`).then((r) => {
        this.cart = r.data.data;
      });
    },

    showExchangeModal() {
      this.exchangeForm = { email: "", searchedEmails: [], searchedEmail: "", errors: [] };
      this.$modal.show("exchange-modal");
    },

    searchUser: _.debounce(function (q) {
      axios.get(`${config("BACKEND_ROOT")}/admin/users?q=${q}`).then((r) => {
        this.exchangeForm.searchedEmails = r.data.data.map((u) => u.email);
      });
    }, 500),

    selectSearchedUser(email) {
      this.exchangeForm.searchedEmail = this.exchangeForm.email = email;
    },

    exchange() {
      if(this.exchangeForm.email === ""){
        alert("please choose the customer Email");
      }
      else{
        if (!confirm(`Deposit ${this.gift.meta.sub_total} AED into account of ${this.exchangeForm.email}?`)) return;
        this.loaded = false;
        axios
            .post(`${this.url}/gifts/${this.id}/exchange`, { email: this.exchangeForm.email })
            .then((r) => { this.gift = r.data.data; this.loaded = true; })
            .catch((e) => { alert(e.response.data?.meta?.message); this.loaded = true; });
      }

    },

    print() {
      const headstr = "<html><head><title></title></head><body>";
      const footstr = "</body>";
      const newstr  = document.all.item("card").innerHTML;
      const oldstr  = document.body.innerHTML;
      document.body.innerHTML = headstr + newstr + footstr;
      window.print();
      document.body.innerHTML = oldstr;
    },

    cancel() {
      if (!confirm("Do you want to cancel this gift?")) return;
      if (!confirm("It'll be disabled forever, are you sure?")) return;
      this.loaded = false;
      axios
          .post(`${this.url}/gifts/${this.id}/cancel`)
          .then((r) => { this.gift = r.data.data; this.loaded = true; })
          .catch((e) => { alert(e.response.data?.meta?.message); this.loaded = true; });
    },

    changePrice() {
      const newAmount = prompt("Enter the new sub total...");
      if (newAmount == null) return;
      if (!/^\d+(\.\d+)?$/.test(newAmount) || newAmount < 0) return alert("Please enter a valid number!");
      this.loaded = false;
      axios
          .patch(`${this.url}/gifts/${this.id}`, { field: "meta->sub_total", value: newAmount })
          .then((r) => { this.gift = r.data.data; this.loaded = true; })
          .catch((e) => { alert(e.response.data?.meta?.message); this.loaded = true; });
    },

    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,"0")}`;
    },
  },

  computed: {
    statusBadge() {
      const map = {
        Dated:     ["status-badge status-badge--green", "Dated"],
        Open:      ["status-badge status-badge--blue",  "Open"],
        Exchanged: ["status-badge status-badge--amber", "Exchanged"],
        Canceled:  ["status-badge status-badge--red",   "Canceled"],
        Expired:   ["status-badge status-badge--gray",  "Expired"],
      };
      const [cls, label] = map[this.gift.status] ?? ["status-badge status-badge--gray", this.gift.status];
      return `<span class="${cls}">${label}</span>`;
    },

    giftExpirationHistory() {
      if (!this.gift.meta?.expiration_history) return "No history";
      return this.gift.meta.expiration_history
          .map((i) => `${i.old_expiration_date} → ${i.new_expiration_date}<br>${i.cost} AED${i.payment_method ? " by " + i.payment_method : ""}<br>───`)
          .join("<br>");
    },

    passengers() {
      const p = this.gift.details.passengers;
      if (!p) return "—";
      if (Number.isInteger(p)) return p;
      return p.map((x) => `${x.title} × ${x.count}`).join(" | ");
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap { display: flex; flex-direction: column; gap: 16px; }

/* ── Header ── */
.page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }
.btn-back { width: 34px; height: 34px; margin-top: 4px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: border-color .15s, color .15s; }
.btn-back:hover { border-color: var(--accent); color: var(--accent); }
.page-title  { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 6px; }
.page-meta   { display: flex; flex-wrap: wrap; gap: 12px; align-items: center; }
.meta-item   { font-size: 12px; color: var(--text-3); display: flex; align-items: center; gap: 5px; }
.meta-item--clickable { cursor: pointer; color: var(--accent); }
.meta-item--clickable:hover { text-decoration: underline; }
.touched-badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 20px; font-size: 11px; background: rgba(245,158,11,.12); color: #f59e0b; border: 1px solid rgba(245,158,11,.25); }

/* ── Info grid ── */
.info-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px; }
.info-grid--sm { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.info-card { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1rem 1.1rem; transition: border-color .15s; }
.info-card--link { cursor: pointer; }
.info-card--link:hover { border-color: var(--accent); }
.info-card__label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 6px; }
.info-card__value { font-size: 14px; font-weight: 500; color: var(--text-1); }
.mono { font-family: monospace; font-size: 13px; }
.link-cell { color: var(--accent); cursor: pointer; font-weight: 500; }
.link-cell:hover { text-decoration: underline; }

/* ── Panel shared ── */
.panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.25rem; }
.panel-title { font-size: 12px; font-weight: 600; color: var(--text-3); text-transform: uppercase; letter-spacing: .5px; margin-bottom: 1.1rem; display: flex; align-items: center; gap: 7px; }

/* ── Table ── */
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); text-align: left; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); }
.dd-table tr:last-child td { border-bottom: none; }
.item-title { font-weight: 500; color: var(--text-1); }

/* ── Message ── */
.message-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 1rem; }
.message-field--full { grid-column: span 2; }
.message-field__label { font-size: 11px; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 4px; }
.message-field__value { font-size: 13px; font-weight: 500; color: var(--text-1); }
.message-field__value--msg { font-weight: 400; color: var(--text-2); line-height: 1.6; padding: 10px; background: var(--bg-hover); border-radius: 8px; border: 1px solid var(--border); }
.btn-print { height: 32px; padding: 0 14px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 12px; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; transition: border-color .15s, color .15s; }
.btn-print:hover { border-color: var(--border-2); color: var(--text-1); }

/* ── Comments ── */
.comments-text { font-size: 13px; color: var(--text-2); line-height: 1.6; padding: 10px; background: var(--bg-hover); border-radius: 8px; border: 1px solid var(--border); }

/* ── Transaction ── */
.transaction-row { display: flex; align-items: center; flex-wrap: wrap; gap: 16px; }
.transaction-amount { font-size: 14px; font-weight: 500; color: var(--text-1); }

/* ── Actions bar ── */
.actions-bar { display: flex; gap: 10px; flex-wrap: wrap; }
.action-btn { height: 38px; padding: 0 16px; border-radius: 9px; font-size: 13px; font-weight: 500; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; transition: opacity .15s, transform .1s; border: 1px solid transparent; }
.action-btn:active { transform: scale(.98); }
.action-btn--red       { background: rgba(244,47,75,.12);  color: #f42f4b; border-color: rgba(244,47,75,.3); }
.action-btn--red:hover { background: rgba(244,47,75,.2); }
.action-btn--green       { background: rgba(34,197,94,.12);  color: #22c55e; border-color: rgba(34,197,94,.3); }
.action-btn--green:hover { background: rgba(34,197,94,.2); }
.action-btn--amber       { background: rgba(245,158,11,.12); color: #f59e0b; border-color: rgba(245,158,11,.3); }
.action-btn--amber:hover { background: rgba(245,158,11,.2); }
.action-btn--secondary   { background: var(--bg-hover); color: var(--text-2); border-color: var(--border); }
.action-btn--secondary:hover { border-color: var(--border-2); color: var(--text-1); }

/* ── Modal ── */
.dd-modal { background: var(--bg-panel); border-radius: 12px; overflow: hidden; }
.dd-modal__header { display: flex; align-items: center; justify-content: space-between; padding: 1rem 1.25rem; border-bottom: 1px solid var(--border); }
.dd-modal__title  { font-size: 15px; font-weight: 500; color: var(--text-1); }
.dd-modal__close  { width: 28px; height: 28px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 6px; color: var(--text-2); cursor: pointer; display: flex; align-items: center; justify-content: center; transition: border-color .15s; }
.dd-modal__close:hover { border-color: var(--accent); color: var(--accent); }
.dd-modal__body   { padding: 1.25rem; }
.dd-modal__desc   { font-size: 13px; color: var(--text-2); margin-bottom: 1rem; }
.dd-modal__footer { display: flex; justify-content: flex-end; gap: 8px; padding: .75rem 1.25rem; border-top: 1px solid var(--border); }

.radio-group { display: flex; flex-direction: column; gap: 8px; margin-bottom: 1rem; }
.radio-option { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px; border: 1px solid var(--border); cursor: pointer; font-size: 13px; color: var(--text-2); transition: border-color .15s; }
.radio-option:hover { border-color: var(--accent); }
.radio-option input { accent-color: var(--accent); }

.divider-label { font-size: 11px; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin: 12px 0 8px; }
.error-list { margin-top: 10px; padding-left: 16px; color: var(--accent); font-size: 13px; }

.btn-cancel { height: 36px; padding: 0 16px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; }
.btn-submit { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-submit:hover { opacity: .88; }
.btn-submit:disabled { opacity: .4; cursor: not-allowed; }
.btn-submit--red { background: #f42f4b; }

/* ══════════════════════════════════════
   Responsive
══════════════════════════════════════ */
@media (max-width: 1024px) { .info-grid { grid-template-columns: repeat(2, 1fr); } }
@media (max-width: 600px)  { .info-grid { grid-template-columns: 1fr 1fr; } .message-grid { grid-template-columns: 1fr; } .message-field--full { grid-column: span 1; } }
</style>